// src/features/form/helpers.ts
// Pure helpers for the form module — no React imports.

import type { Entity } from '@core/types/entity';
import type { FormFieldType, FormStatus, FormSubmissionStatus } from '@core/types/form';

export interface ScopeIds {
  platformId: string;
  productId: string;
  tenantId: string;
}

/** Build a fully-populated Entity reference from an id and the current scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeIds): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

/** Extract an error message suitable for display. */
export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export const FORM_STATUSES: FormStatus[] = [
  'FORM_STATUS_DRAFT',
  'FORM_STATUS_PUBLISHED',
  'FORM_STATUS_CLOSED',
  'FORM_STATUS_ARCHIVED',
];

export function formStatusBadgeVariant(status?: FormStatus): 'default' | 'success' | 'warning' | 'secondary' {
  switch (status) {
    case 'FORM_STATUS_PUBLISHED':
      return 'success';
    case 'FORM_STATUS_CLOSED':
      return 'warning';
    case 'FORM_STATUS_ARCHIVED':
      return 'secondary';
    default:
      return 'default';
  }
}

export const SUBMISSION_STATUSES: FormSubmissionStatus[] = [
  'FORM_SUBMISSION_STATUS_DRAFT',
  'FORM_SUBMISSION_STATUS_SUBMITTED',
  'FORM_SUBMISSION_STATUS_REVIEWED',
  'FORM_SUBMISSION_STATUS_APPROVED',
  'FORM_SUBMISSION_STATUS_REJECTED',
];

export function submissionStatusBadgeVariant(status?: FormSubmissionStatus): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'FORM_SUBMISSION_STATUS_APPROVED':
      return 'success';
    case 'FORM_SUBMISSION_STATUS_REJECTED':
      return 'error';
    case 'FORM_SUBMISSION_STATUS_SUBMITTED':
      return 'warning';
    default:
      return 'default';
  }
}

/** Field types that carry a fixed option list (select / multi-select / radio). */
export const OPTION_FIELD_TYPES: FormFieldType[] = [
  'FORM_FIELD_TYPE_SELECT',
  'FORM_FIELD_TYPE_MULTI_SELECT',
  'FORM_FIELD_TYPE_RADIO',
];

/** Field types with no answer of their own — layout/meta only. */
export const NON_ANSWER_FIELD_TYPES: FormFieldType[] = [
  'FORM_FIELD_TYPE_SECTION_BREAK',
  'FORM_FIELD_TYPE_RICH_TEXT',
  'FORM_FIELD_TYPE_HIDDEN',
];

export const FIELD_TYPE_ORDER: FormFieldType[] = [
  'FORM_FIELD_TYPE_TEXT',
  'FORM_FIELD_TYPE_TEXTAREA',
  'FORM_FIELD_TYPE_NUMBER',
  'FORM_FIELD_TYPE_EMAIL',
  'FORM_FIELD_TYPE_PHONE',
  'FORM_FIELD_TYPE_URL',
  'FORM_FIELD_TYPE_DATE',
  'FORM_FIELD_TYPE_TIME',
  'FORM_FIELD_TYPE_DATETIME',
  'FORM_FIELD_TYPE_SELECT',
  'FORM_FIELD_TYPE_MULTI_SELECT',
  'FORM_FIELD_TYPE_RADIO',
  'FORM_FIELD_TYPE_CHECKBOX',
  'FORM_FIELD_TYPE_FILE_UPLOAD',
  'FORM_FIELD_TYPE_RATING',
  'FORM_FIELD_TYPE_SCALE',
  'FORM_FIELD_TYPE_NPS',
  'FORM_FIELD_TYPE_MATRIX',
  'FORM_FIELD_TYPE_SIGNATURE',
  'FORM_FIELD_TYPE_LOCATION',
  'FORM_FIELD_TYPE_SECTION_BREAK',
  'FORM_FIELD_TYPE_RICH_TEXT',
  'FORM_FIELD_TYPE_HIDDEN',
];

export function parseClearableInt(raw: string): number | undefined {
  const digits = raw.replace(/[^0-9]/g, '');
  return digits === '' ? undefined : parseInt(digits, 10);
}
